import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import DatePicker, { registerLocale } from 'react-datepicker'
import moment from 'moment'
import { el, tr } from 'date-fns/locale'
import ContentSection from '../../components/UI/ContentSection'
import CustomDatePicker from '../../components/UI/CustomDatePicker'
import TableToolbar from '../../components/Table/TableToolbar'
import FilterSelect from '../../components/Table/FilterSelect'
import CancellationsList, {
  cancellationCsvColumns,
} from '../../components/CancellationsList/CancellationsList'
import useCancellations from '../../services/payment/useCancellations'
import { downloadCsv } from '../../utils/toCsv'
import {
  CANCELLATION_REASON_KEYS,
  cancellationReasonI18nKey,
} from '../../langs/cancellationReasons'
import 'react-datepicker/dist/react-datepicker.css'

registerLocale('el', el)
registerLocale('tr', tr)

/**
 * The cancellation feedback list.
 *
 * Unlike the other two super admin tables, every filter here is applied by the
 * backend - this is the first of these endpoints whose query params cover the
 * whole toolbar. Sorting stays with the table: the response is unpaginated, so
 * react-table is ordering the complete result set rather than a page of it,
 * and `?ordering=` has nothing to offer for the plan, who-cancelled or comment
 * columns. Doing half the headers each way would make the table feel broken.
 */
const Cancellations = () => {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const organization = searchParams.get('organization')
  const [search, setSearch] = useState('')
  const [reason, setReason] = useState('')
  const [reverted, setReverted] = useState('')
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ])
  const [startDate, endDate] = dateRange

  const { data: cancellations, isLoading } = useCancellations({
    reason,
    reverted,
    organization,
    startDate,
    endDate,
    search,
  })

  useEffect(() => {
    document.title = `${t('cancellations')} - ReportAll`
  }, [t])

  // The eight choices the backend accepts, not just the ones the current rows
  // happen to use: asking for a reason nobody picked and being told there are
  // none is an answer.
  const reasonOptions = useMemo(
    () =>
      CANCELLATION_REASON_KEYS.map((key) => ({
        value: key,
        label: t(cancellationReasonI18nKey(key)),
      })),
    [t],
  )

  const revertedOptions = useMemo(
    () => [
      { value: 'false', label: t('stillStanding') },
      { value: 'true', label: t('reversed') },
    ],
    [t],
  )

  const csvColumns = useMemo(() => cancellationCsvColumns(t), [t])
  const rows = useMemo(() => cancellations ?? [], [cancellations])

  const handleExport = () => {
    downloadCsv(
      `cancellations-${moment().format('YYYY-MM-DD')}.csv`,
      csvColumns,
      rows,
    )
  }

  return (
    <ContentSection>
      <TableToolbar
        title={t('cancellations')}
        count={rows.length}
        search={search}
        onSearchChange={setSearch}
        filters={
          <>
            <FilterSelect
              allLabel={t('allReasons')}
              value={reason}
              options={reasonOptions}
              onChange={setReason}
            />
            <FilterSelect
              allLabel={t('allCancellations')}
              value={reverted}
              options={revertedOptions}
              onChange={setReverted}
            />
          </>
        }
        onExport={handleExport}
        isExportDisabled={rows.length === 0}
        leading={
          <DatePicker
            locale={
              i18n.language === 'el'
                ? 'el'
                : i18n.language === 'tr'
                  ? 'tr'
                  : 'en'
            }
            dateFormat='dd MMM yy'
            calendarClassName='bg-white'
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={(update) =>
              setDateRange(update as [Date | null, Date | null])
            }
            customInput={
              <CustomDatePicker
                variant='pill'
                emptyLabel={t('selectDateRange')}
              />
            }
          />
        }
      />
      <CancellationsList isLoading={isLoading} cancellations={rows} />
    </ContentSection>
  )
}

export default Cancellations
